import {
    FunctionComponent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { fetchAuthWrapper } from '@starjack/api';
import { urlBuilder } from '@starjack/utils';
import { useNotification } from '@starjack/features/notifications';
import { Dropzone } from '@starjack/shared-components';

export type PdfBackgroundName = 'eticket' | 'pickupticket' | 'barcodeticket';

type UploadState = 'loading' | 'empty' | 'new' | 'existing';

type TicketTemplateTabProps = {
    name: PdfBackgroundName;
    infoText?: string;
    extraInfoText?: string;
};

const getPdfBackgroundUrl = (name: PdfBackgroundName) =>
    urlBuilder('order', `pdfbackgrounds/${name}`);

const ACCEPTED_PDF = { 'application/pdf': ['.pdf'] };

export const TicketTemplateTab: FunctionComponent<TicketTemplateTabProps> = ({
    name,
    infoText,
    extraInfoText,
}) => {
    const [uploadState, setUploadState] = useState<UploadState>('loading');
    const [newFile, setNewFile] = useState<File | null>(null);
    const [existingBlob, setExistingBlob] = useState<Blob | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<
        'save' | 'delete' | null
    >(null);
    const [replaceModalOpen, setReplaceModalOpen] = useState(false);
    const fetchedRef = useRef(false);
    const { addNotification } = useNotification();

    const objectUrl = useMemo(() => {
        const blob = newFile ?? existingBlob;
        if (!blob) return null;
        return URL.createObjectURL(blob);
    }, [newFile, existingBlob]);

    useEffect(() => {
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [objectUrl]);

    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;

        const fetchExisting = async () => {
            try {
                const response = await fetchAuthWrapper(
                    getPdfBackgroundUrl(name),
                    {
                        headers: { Accept: 'application/pdf' },
                    }
                );
                if (!response.ok || response.status === 204) {
                    setUploadState('empty');
                    return;
                }
                const blob = await response.blob();
                if (blob.size === 0) {
                    setUploadState('empty');
                    return;
                }
                setExistingBlob(blob);
                setUploadState('existing');
            } catch {
                setUploadState('empty');
            }
        };

        fetchExisting();
    }, [name]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;
        setNewFile(acceptedFiles[0]);
        setReplaceModalOpen(false);
        setUploadState('new');
    }, []);

    const handleSaveClick = () => {
        setPendingAction('save');
        setConfirmDialogOpen(true);
    };

    const handleDeleteClick = () => {
        setPendingAction('delete');
        setConfirmDialogOpen(true);
    };

    const handleDialogCancel = () => {
        setConfirmDialogOpen(false);
        setPendingAction(null);
    };

    const handleDialogConfirm = async () => {
        setConfirmDialogOpen(false);

        if (pendingAction === 'save' && newFile) {
            await handleSave(newFile);
        } else if (pendingAction === 'delete') {
            await handleDelete();
        }

        setPendingAction(null);
    };

    const handleSave = async (file: File) => {
        setIsSubmitting(true);
        try {
            const arrayBuffer = await file.arrayBuffer();
            const response = await fetchAuthWrapper(getPdfBackgroundUrl(name), {
                method: 'POST',
                body: arrayBuffer,
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/pdf',
                },
            });
            if (!response.ok) {
                throw new Error();
            }
            setExistingBlob(
                new Blob([arrayBuffer], { type: 'application/pdf' })
            );
            setNewFile(null);
            setUploadState('existing');
            addNotification({
                message: 'Vorlage erfolgreich gespeichert.',
                type: 'success',
            });
        } catch {
            addNotification({
                message:
                    'Fehler beim Speichern. Bitte versuchen Sie es später noch einmal.',
                type: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setIsSubmitting(true);
        try {
            const response = await fetchAuthWrapper(getPdfBackgroundUrl(name), {
                method: 'DELETE',
            });
            if (!response.ok) {
                throw new Error();
            }
            setExistingBlob(null);
            setNewFile(null);
            setReplaceModalOpen(false);
            setUploadState('empty');
            addNotification({
                message: 'Vorlage erfolgreich gelöscht.',
                type: 'success',
            });
        } catch {
            addNotification({
                message:
                    'Fehler beim Löschen. Bitte versuchen Sie es später noch einmal.',
                type: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClearSelection = () => {
        setNewFile(null);
        setUploadState(existingBlob ? 'existing' : 'empty');
    };

    const confirmDialogMessage =
        pendingAction === 'save'
            ? 'Diese Vorlage wird sofort für alle neuen Tickets verwendet. Fortfahren?'
            : 'Die benutzerdefinierte Vorlage wird gelöscht und die Standardvorlage wird verwendet. Fortfahren?';

    return (
        <Stack spacing={3} sx={{ pt: 2 }}>
            {infoText && (
                <Typography variant='body2' color='text.secondary'>
                    {infoText}
                </Typography>
            )}
            {extraInfoText && (
                <Typography variant='body2' color='warning.main'>
                    {extraInfoText}
                </Typography>
            )}

            {uploadState === 'loading' && (
                <Box display='flex' justifyContent='center' py={4}>
                    <CircularProgress />
                </Box>
            )}

            {uploadState === 'empty' && (
                <Dropzone
                    onDrop={onDrop}
                    accept={ACCEPTED_PDF}
                    multiple={false}
                />
            )}

            {(uploadState === 'new' || uploadState === 'existing') &&
                objectUrl && (
                    <Box
                        component='iframe'
                        src={objectUrl}
                        sx={{
                            width: '100%',
                            height: 600,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                        }}
                        title='PDF Vorschau'
                    />
                )}

            {uploadState === 'new' && (
                <Stack direction='row' spacing={2}>
                    <Button
                        variant='contained'
                        onClick={handleSaveClick}
                        disabled={isSubmitting}
                    >
                        Speichern
                    </Button>
                    <Button
                        variant='outlined'
                        onClick={handleClearSelection}
                        disabled={isSubmitting}
                    >
                        Auswahl löschen
                    </Button>
                </Stack>
            )}

            {uploadState === 'existing' && (
                <Box display='flex' justifyContent='flex-end' gap={2}>
                    <Button
                        variant='outlined'
                        onClick={() => setReplaceModalOpen(true)}
                        disabled={isSubmitting}
                    >
                        Vorlage ersetzen
                    </Button>
                    <Button
                        variant='outlined'
                        color='error'
                        onClick={handleDeleteClick}
                        disabled={isSubmitting}
                    >
                        Vorlage löschen
                    </Button>
                </Box>
            )}

            <Dialog
                open={replaceModalOpen}
                onClose={() => setReplaceModalOpen(false)}
                maxWidth='sm'
                fullWidth
            >
                <DialogTitle>Vorlage ersetzen</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Dropzone
                            onDrop={onDrop}
                            accept={ACCEPTED_PDF}
                            multiple={false}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReplaceModalOpen(false)}>
                        Abbrechen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDialogOpen} onClose={handleDialogCancel}>
                <DialogTitle>Bestätigung</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialogMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogCancel}>Abbrechen</Button>
                    <Button
                        onClick={handleDialogConfirm}
                        variant='contained'
                        autoFocus
                    >
                        Fortfahren
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};
